import { useMemo } from "react";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CardSection from '../CardSection';
import BannerDynamic from '../BannerDynamic';

import { useApp } from '../../../context';
import { selectAds, adFromListing } from '../../../utils/advertising';

const SearchResults = ({ providers, title }) => {
  const { adverts, serviceListing, listingsLoaded } = useApp();

  const listingsById = useMemo(() => {
    const map = {};
    (serviceListing || []).forEach((l) => { map[l.id] = l; });
    return map;
  }, [serviceListing]);

  const groups = useMemo(() => Object.keys(providers || {}), [providers]);

  /**
   * One banner per category group: a sold SEARCH_BANNER if there is one, else
   * a listing from that group as house inventory.
   *
   * The house pick is DERIVED and stable per group rather than recomputed with
   * `Math.random()` inside the render, which is what it used to be — every
   * keystroke, every snapshot and every unrelated re-render swapped the banner
   * out from under whoever was reading it. It also indexed
   * `length - 1`, so the last listing in a group could never be featured and a
   * single-listing group produced index 0 by luck rather than intent.
   *
   * Keyed off the group name so the same category shows the same listing for
   * as long as the results hold still.
   */
  const bannerFor = useMemo(() => {
    const map = {};
    groups.forEach((key) => {
      const sold = selectAds(adverts, 'SEARCH_BANNER', { listingsById, listingsLoaded })[0];
      if (sold) {
        map[key] = sold;
        return;
      }
      const list = providers[key] || [];
      if (!list.length) {
        map[key] = null;
        return;
      }
      // Deterministic: sum of the group name's char codes, modulo the length.
      const seed = [...key].reduce((n, c) => n + c.charCodeAt(0), 0);
      map[key] = adFromListing(list[seed % list.length]);
    });
    return map;
  }, [groups, providers, adverts, listingsById, listingsLoaded]);

  return (
    <Box>
      <Typography component="div" className='txt--gray' sx={{ margin: '0 0 1rem 1rem' }}>
        Search Results: {title}
      </Typography>

      {groups.map((key, i) => (
        <div key={`search-results-${key}-${i}`}>
          <BannerDynamic ad={bannerFor[key]} />
          <CardSection title={key} serviceProviders={providers[key]} />
        </div>
      ))}
    </Box>
  );
}

export default SearchResults;
